import React, { useState, useEffect } from "react";
import axios from "axios";
import { 
  FiMail, 
  FiSearch, 
  FiMessageSquare, 
  FiUser, 
  FiArrowLeft,
  FiClock,
  FiRefreshCw
} from "react-icons/fi";
import { db } from "../lib/firebase";
import { 
  collection, 
  query, 
  getDocs, 
  orderBy, 
  limit as fsLimit, 
  doc, 
  getDoc,
  where
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";

const Chats = () => {
  const [loading, setLoading] = useState(true);
  const [dbError, setDbError] = useState(null);
  const [chats, setChats] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedChat, setSelectedChat] = useState(null);
  const [chatMessages, setChatMessages] = useState({ list: [], loading: false });
  const navigate = useNavigate();
  const messagesEndRef = React.useRef(null);

  const fetchAllChats = async () => {
    if (!db) {
      console.error("Firestore DB not initialized. Check your environment variables.");
      setDbError("Firebase Firestore is not initialized. Please check your configuration.");
      setLoading(false);
      return;
    }
    setDbError(null);
    try {
      setLoading(true);
      
      // Try common collection names just in case
      const collectionsToTry = ["chats", "chat", "conversations", "conversation"];
      let allChatDocs = [];

      for (const colName of collectionsToTry) {
        try {
          const colRef = collection(db, colName);
          const q = query(colRef, fsLimit(100));
          const snap = await getDocs(q);
          if (!snap.empty) {
            console.log(`Found ${snap.docs.length} docs in '${colName}' collection`);
            allChatDocs = [...allChatDocs, ...snap.docs];
          }
        } catch (err) {
          console.warn(`Error querying collection '${colName}':`, err);
        }
      }

      if (allChatDocs.length === 0) {
        setChats([]);
        setLoading(false);
        return;
      }

      // 1. Collect all unique participant IDs
      const uniqueUserIds = new Set();
      allChatDocs.forEach(d => {
        const data = d.data();
        (data.participants || []).forEach(pid => uniqueUserIds.add(String(pid)));
      });

      // 2. Fetch all unique user details in parallel
      const userCache = new Map();
      await Promise.all(Array.from(uniqueUserIds).map(async (sid) => {
        let userData = { id: sid, name: "Unknown", image: "" };
        try {
          console.log(`Fetching user data for ID: ${sid}`);
          
          // Try 1: Firestore by document ID
          const uDoc = await getDoc(doc(db, "users", sid));
          if (uDoc.exists()) {
            const d = uDoc.data();
            console.log(`Found user by doc ID ${sid}:`, d.fullName || d.name);
            userData = {
              id: sid,
              name: d.fullName || d.name || d.displayName || "Unknown",
              image: d.image || d.profileImage || d.photoURL || ""
            };
          } else {
            // Try 2: Query by userId field (string and number)
            const uRef = collection(db, "users");
            const uQString = query(uRef, where("userId", "==", sid), fsLimit(1));
            const uQNumber = !isNaN(sid) ? query(uRef, where("userId", "==", Number(sid)), fsLimit(1)) : null;

            const [s1, s2] = await Promise.all([
              getDocs(uQString),
              uQNumber ? getDocs(uQNumber) : Promise.resolve({ empty: true })
            ]);

            const found = !s1.empty ? s1.docs[0] : (!s2.empty ? s2.docs[0] : null);
            if (found) {
              const d = found.data();
              console.log(`Found user by userId query ${sid}:`, d.fullName || d.name);
              userData = {
                id: sid,
                name: d.fullName || d.name || d.displayName || "Unknown",
                image: d.image || d.profileImage || d.photoURL || ""
              };
            } else {
              // Try 3: Query by _id field (MongoDB style)
              const uQ_id = query(uRef, where("_id", "==", sid), fsLimit(1));
              const s3 = await getDocs(uQ_id);
              
              if (!s3.empty) {
                const d = s3.docs[0].data();
                console.log(`Found user by _id query ${sid}:`, d.fullName || d.name);
                userData = {
                  id: sid,
                  name: d.fullName || d.name || d.displayName || "Unknown",
                  image: d.image || d.profileImage || d.photoURL || ""
                };
              } else {
                // Try 4: Backend API
                try {
                  console.log(`Trying backend API for user ${sid}`);
                  const res = await axios.get(`/api/v1/admin/users/${sid}`);
                  const apiUser = res?.data?.data || res?.data;
                  if (apiUser) {
                    console.log(`Found user via API ${sid}:`, apiUser.fullName || apiUser.name);
                    userData = {
                      id: sid,
                      name: apiUser.fullName || apiUser.name || apiUser.displayName || "Unknown",
                      image: apiUser.image || apiUser.profileImage || apiUser.photoURL || ""
                    };
                  } else {
                    console.warn(`No user data found for ${sid} - keeping as Unknown`);
                  }
                } catch (apiErr) {
                  console.warn(`Backend API fetch failed for user ${sid}:`, apiErr.message);
                }
              }
            }
          }
        } catch (e) {
          console.error(`Error fetching user ${sid}:`, e);
        }
        userCache.set(sid, userData);
      }));

      // 3. Build chat list using cached user data
      const chatList = allChatDocs.map(d => {
        const data = d.data();
        const participantIds = data.participants || [];
        const participantsData = participantIds.map(pid => userCache.get(String(pid)) || { id: String(pid), name: "Unknown", image: "" });

        // Get the best possible timestamp for sorting
        const getTimestamp = (data) => {
          const ts = data.updatedAt || data.lastMessage?.timestamp || data.createdAt;
          if (!ts) return 0;
          if (ts.toDate) return ts.toDate().getTime();
          if (ts instanceof Date) return ts.getTime();
          const parsed = new Date(ts).getTime();
          return isNaN(parsed) ? 0 : parsed;
        };

        return {
          id: d.id,
          collectionName: d.ref.parent.id,
          ...data,
          participantsData,
          sortTimestamp: getTimestamp(data)
        };
      });
      
      // 4. Sort manually by recents at top (descending)
      const sortedList = [...chatList].sort((a, b) => b.sortTimestamp - a.sortTimestamp);
      setChats(sortedList);
    } catch (e) {
      console.error("Error fetching all chats:", e);
    } finally {
      setLoading(false);
    }
  };

  const fetchChatMessages = async (chatId, collectionName = "chats") => {
    if (!db || !chatId) return;
    try {
      setChatMessages({ list: [], loading: true });
      const chatRef = doc(db, collectionName, chatId);
      const messagesRef = collection(chatRef, "messages");
      const q = query(messagesRef, orderBy("timestamp", "asc"));
      const snap = await getDocs(q);
      const msgs = snap.docs.map(d => ({
        id: d.id,
        ...d.data(),
        timestamp: d.data().timestamp?.toDate?.() || new Date(d.data().timestamp || 0)
      }));
      setChatMessages({ list: msgs, loading: false });
    } catch (e) {
      console.error(`Error fetching messages from ${collectionName}/${chatId}/messages:`, e);
      setChatMessages({ list: [], loading: false });
    }
  };

  useEffect(() => {
    fetchAllChats();
  }, []);

  const filteredChats = chats.filter(chat => {
    const queryStr = search.toLowerCase();
    return chat.participantsData.some(p => p.name.toLowerCase().includes(queryStr)) ||
           (chat.lastMessage?.text || "").toLowerCase().includes(queryStr);
  });

  const formatDate = (date) => {
    if (!date) return "—";
    const ts = date.toDate ? date.toDate() : new Date(date);
    if (isNaN(ts.getTime())) return "—";
    return ts.toLocaleString();
  };

  return (
    <div className="min-h-screen bg-white p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-extrabold text-[var(--primary-solid)] flex items-center gap-3">
              <FiMail className="text-3xl" />
              Global Chats
            </h1>
            <p className="text-[var(--black-text)]/70 mt-2">
              Monitor all user-to-user conversations across the platform
            </p>
          </div>
          
          <button 
            onClick={fetchAllChats}
            className="p-2 bg-[var(--primary-solid)]/10 text-[var(--primary-solid)] rounded-lg hover:bg-[var(--primary-solid)]/20 transition"
            title="Refresh Chats"
          >
            <FiRefreshCw className={loading ? "animate-spin" : ""} />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 h-[calc(100vh-220px)]">
          {/* Chat List Side */}
          <div className={`${selectedChat ? 'hidden lg:flex' : 'flex'} lg:col-span-4 flex-col bg-white border-2 border-gray-200 rounded-2xl overflow-hidden shadow-sm`}>
            <div className="p-4 border-b border-gray-100">
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--primary-solid)]" />
                <input 
                  type="text"
                  placeholder="Search participants or messages..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-gray-50 border-2 border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[var(--primary-solid)]/50 focus:border-[var(--primary-solid)] text-sm"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto custom-scrollbar p-2 space-y-2">
              {dbError ? (
                <div className="text-center py-20 px-4">
                  <div className="bg-red-50 text-red-600 p-4 rounded-xl border border-red-100 text-sm">
                    {dbError}
                  </div>
                </div>
              ) : loading ? (
                <div className="flex justify-center py-20">
                  <div className="w-8 h-8 border-2 border-[var(--primary-solid)] border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : filteredChats.length === 0 ? (
                <div className="text-center py-20 opacity-60">
                  <FiMessageSquare className="mx-auto text-4xl mb-3" />
                  <p>No conversations found</p>
                </div>
              ) : (
                filteredChats.map(chat => (
                  <div 
                    key={chat.id}
                    onClick={() => {
                      setSelectedChat(chat);
                      fetchChatMessages(chat.id, chat.collectionName);
                    }}
                    className={`p-3 rounded-xl border-2 transition cursor-pointer flex items-center gap-3 ${
                      selectedChat?.id === chat.id 
                        ? 'border-[var(--primary-solid)] bg-[var(--primary-solid)]/5' 
                        : 'border-transparent hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex -space-x-4">
                      {chat.participantsData.slice(0, 2).map((p, i) => (
                        <img 
                          key={i}
                          src={p.image || "https://via.placeholder.com/40"}
                          alt={p.name}
                          className="w-10 h-10 rounded-full border-2 border-white object-cover shadow-sm bg-gray-200"
                        />
                      ))}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start">
                        <h4 className="font-bold text-sm truncate text-[var(--black-text)]">
                          {chat.participantsData.map(p => p.name).join(", ")}
                        </h4>
                      </div>
                      <p className="text-xs text-[var(--black-text)]/70 truncate mt-1">
                        {chat.lastMessage?.text || "No messages yet"}
                      </p>
                      <div className="flex items-center gap-1 mt-1 opacity-50 text-[10px]">
                          <FiClock />
                          {formatDate(chat.updatedAt || chat.lastMessage?.timestamp || chat.createdAt)}
                        </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Chat Conversation Side */}
          <div className={`${!selectedChat ? 'hidden lg:flex' : 'flex'} lg:col-span-8 flex-col bg-white border-2 border-gray-200 rounded-2xl overflow-hidden shadow-sm`}>
            {selectedChat ? (
              <>
                {/* Chat Header */}
                <div className="p-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <button 
                      onClick={() => setSelectedChat(null)}
                      className="lg:hidden p-2 hover:bg-gray-200 rounded-full transition"
                    >
                      <FiArrowLeft />
                    </button>
                    <div className="flex -space-x-3">
                      {selectedChat.participantsData.map((p, i) => (
                        <img 
                          key={i}
                          src={p.image || "https://via.placeholder.com/40"}
                          alt={p.name}
                          className="w-8 h-8 rounded-full border-2 border-white object-cover shadow-sm"
                        />
                      ))}
                    </div>
                    <div>
                      <h3 className="font-bold text-[var(--black-text)]">
                        {selectedChat.participantsData.map(p => p.name).join(", ")}
                      </h3>
                      <p className="text-[10px] text-[var(--black-text)]/60">
                        {selectedChat.id}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Messages Body */}
                <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gray-50/30 custom-scrollbar">
                  {chatMessages.loading ? (
                    <div className="flex justify-center py-20">
                      <div className="w-8 h-8 border-2 border-[var(--primary-solid)] border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  ) : chatMessages.list.length === 0 ? (
                    <div className="text-center py-20 opacity-60">
                      <p>No messages in this thread</p>
                    </div>
                  ) : (
                    chatMessages.list.map((msg) => {
                      const sender = selectedChat.participantsData.find(p => String(p.id) === String(msg.senderId));
                      // Consistently put the first participant on the left and the second on the right
                      const isLeft = String(msg.senderId) === String(selectedChat.participantsData[0]?.id);

                      return (
                        <div key={msg.id} className={`flex flex-col ${isLeft ? 'items-start' : 'items-end'}`}>
                          <div className={`flex items-end gap-2 max-w-[80%] ${isLeft ? 'flex-row' : 'flex-row-reverse'}`}>
                            <img 
                              src={sender?.image || "https://via.placeholder.com/30"}
                              alt={sender?.name}
                              className="w-6 h-6 rounded-full object-cover border border-gray-200 shadow-sm mb-1"
                            />
                            <div className={`p-3 rounded-2xl shadow-sm ${
                              isLeft 
                                ? 'bg-white border border-gray-100 rounded-bl-none text-[var(--black-text)]' 
                                : 'bg-[var(--primary-solid)] text-white rounded-br-none'
                            }`}>
                              <p className="text-sm">{msg.text}</p>
                              <div className={`text-[10px] mt-1 opacity-60 ${!isLeft ? 'text-right' : ''}`}>
                                {msg.timestamp.toLocaleString()}
                              </div>
                            </div>
                          </div>
                          <p className="text-[9px] mt-1 text-[var(--black-text)]/40 px-8">
                            {sender?.name || "Unknown"}
                          </p>
                        </div>
                      );
                    })
                  )}
                </div>
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center opacity-40 p-10 text-center">
                <FiMessageSquare className="text-8xl mb-4" />
                <h3 className="text-2xl font-bold">Select a conversation</h3>
                <p>Choose a chat from the sidebar to view the full message history</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chats;
